'use client';

import React, { ReactNode, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import _ from 'lodash';
import { DropdownMenuCheckboxItem, DropdownMenuWithPointerUp } from '@/components/ui/dropdown';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import Debouncer from '@/lib/frontend/utils/debouncer';

import { SearchIndexItem, SearchIndexType, searchIndexTypeLabels } from '@/types/search';
import {
  ChangeSearchFilters,
  defaultFilterTypes,
  HideSearch,
  ShowSearch,
} from '@/lib/frontend/store/actionCreators/SearchActions';
import { Loader, Plus, X } from 'lucide-react';
import Fuse, { FuseResult } from 'fuse.js';
import { SearchCommandItem } from '@/components/frontend/search/search-command-item';
import { fetchSearchIndex } from '@/lib/frontend/utils/search';
import { useAppSelector, useAppStore } from '@/lib/frontend/store/hooks';
import { createSelector } from '@reduxjs/toolkit';
import { selectSearch } from '@/lib/frontend/store/helpers';
import { H4 } from '@/components/ui/typography';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const isMac = typeof window !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

const debouncer = new Debouncer();

const CommandGroupItem = (props: {
  title: string;
  query: string;
  results: FuseResult<SearchIndexItem>[];
}) => {
  return (
    <CommandGroup>
      <H4 className="text-sm px-2 py-2 mb-1">{props.title}</H4>
      <div className="pl-3 ml-3 border-l border-foreground/10">
        {props.results.map((r) => (
          <SearchCommandItem key={`${r.item.id}`} result={r} query={props.query} />
        ))}
      </div>
    </CommandGroup>
  );
};

const selectState = createSelector([selectSearch], (search) => ({
  filterTypes: search.filterTypes,
  open: search.open,
}));

export const SearchDialog = () => {
  const searchState = useAppSelector(selectState);
  const cachedSearchIndexRef = useRef<SearchIndexItem[] | null>(null);

  const [fuseResults, setFuseResults] = React.useState<FuseResult<SearchIndexItem>[]>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [debouncedValue, setDebouncedValue] = React.useState('');
  const fuseRef = useRef<Fuse<SearchIndexItem> | null>(null);
  const store = useAppStore();

  useHotkeys(['mod+k'], (e) => {
    e.preventDefault();
    ShowSearch(store, { filterTypes: defaultFilterTypes });
  });

  const initFuse = (index: SearchIndexItem[]) => {
    if (index) {
      let filteredIndex = index;
      if (searchState.filterTypes?.length) {
        filteredIndex = index.filter((item) => searchState.filterTypes?.includes(item.type));
      }

      fuseRef.current = new Fuse(filteredIndex, {
        keys: ['title', 'content'],
        threshold: 0,
        ignoreLocation: true,
        includeMatches: true,
        includeScore: true,
        ignoreDiacritics: true,
        minMatchCharLength: 3,
      });
    }
  };

  const initIndex = async () => {
    if (!cachedSearchIndexRef.current) {
      cachedSearchIndexRef.current = await fetchSearchIndex();

      setIsInitialized(true);
    }
  };

  useEffect(() => {
    if (cachedSearchIndexRef.current) {
      initFuse(cachedSearchIndexRef.current);
    }
  }, [searchState.filterTypes]);

  const handleOpen = async () => {
    await initIndex();

    if (!cachedSearchIndexRef.current) {
      return;
    }

    initFuse(cachedSearchIndexRef.current);
    setIsInitialized(true);
  };

  useEffect(() => {
    if (searchState.open) {
      handleOpen();
    }
  }, [searchState.open]);

  const search = (query: string) => {
    setIsLoading(true);
    debouncer.run(() => {
      setIsLoading(false);
      if (query === '') {
        setFuseResults(undefined);
        setDebouncedValue('');
        return;
      }

      const results = fuseRef.current?.search(query) || [];

      setFuseResults(results);
      setDebouncedValue(query);
    });
  };

  let emptyMessage: ReactNode = '';

  if (!isInitialized) {
    emptyMessage = (
      <CommandEmpty className="opacity-60 flex justify-center">
        <Loader className="animate-spin" />
      </CommandEmpty>
    );
  } else if (!isLoading && fuseResults && fuseResults.length === 0) {
    emptyMessage = <CommandEmpty className="opacity-60">No results found.</CommandEmpty>;
  } else if (!debouncedValue && !isLoading) {
    emptyMessage = (
      <CommandEmpty className="opacity-60">
        Search QLab <span className="opacity-60 text-sm ml-1">{isMac ? '⌘' : 'Ctrl'}K</span>
      </CommandEmpty>
    );
  } else if (isLoading) {
    emptyMessage = <CommandEmpty className="opacity-60">Searching...</CommandEmpty>;
  }

  return (
    <Dialog
      open={searchState.open}
      onOpenChange={(open) => {
        if (!open) {
          HideSearch(store);
        }
      }}
    >
      <DialogContent
        className="p-0 w-[95%] sm:max-w-lg fixed"
        showCloseButton={false}
        customYPositionClassName="top-[15%] translate-y-[-0%] data-[state=open]:slide-in-from-top-[0%] data-[state=closed]:slide-out-to-top-[0%]"
      >
        <DialogTitle className="hidden">Search QLab</DialogTitle>
        <Command shouldFilter={false} className="bg-[#041333] border border-foreground/20">
          <div className="relative">
            <div className="flex justify-between border-b border-foreground/10">
              <div className="flex gap-2">
                <div className="p-2 flex flex-wrap gap-2 items-center">
                  {searchState.filterTypes?.map((t) => (
                    <Badge
                      variant="outline"
                      className="text-foreground flex gap-1 pr-1 bg-primary/10"
                      key={t}
                    >
                      {searchIndexTypeLabels[t]}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-5 h-5 rounded-full [&_svg]:size-3"
                        onClick={() => {
                          const newFilterTypes = [...(searchState.filterTypes || [])];
                          _.remove(newFilterTypes, (ft) => ft === t);
                          ChangeSearchFilters(store, newFilterTypes);
                        }}
                      >
                        <X />
                      </Button>
                    </Badge>
                  ))}
                  <DropdownMenuWithPointerUp
                    trigger={
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-6 h-6 rounded-full [&_svg]:size-4"
                      >
                        <Plus />
                      </Button>
                    }
                    triggerProps={{ asChild: true }}
                  >
                    {Object.entries(searchIndexTypeLabels).map(([type, label]) => (
                      <DropdownMenuCheckboxItem
                        onSelect={() => {
                          const newFilterTypes = [...(searchState.filterTypes || [])];
                          if (newFilterTypes.includes(type as SearchIndexType)) {
                            _.remove(newFilterTypes, (t) => t === type);
                          } else {
                            newFilterTypes.push(type as SearchIndexType);
                          }

                          ChangeSearchFilters(store, newFilterTypes);
                        }}
                        checked={searchState.filterTypes?.includes(type as SearchIndexType)}
                        key={type}
                      >
                        {label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuWithPointerUp>
                </div>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => HideSearch(store)}
                className="w-6 h-6 flex items-center justify-center m-2"
              >
                <X className="opacity-60" />
              </Button>
            </div>
            <CommandInput
              autoFocus
              onValueChange={(value) => {
                search(value);
              }}
            />
          </div>
          <CommandList className="max-h-[60vh] pb-3 pr-3">
            {emptyMessage}

            {debouncedValue &&
              (fuseResults?.length || 0) > 0 &&
              Object.entries(_.groupBy(fuseResults, (r) => r.item.type)).map(([type, results]) => (
                <CommandGroupItem
                  key={type}
                  title={searchIndexTypeLabels[type as SearchIndexType]}
                  query={debouncedValue}
                  results={results}
                />
              ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
